// src/pages/public/CaseStudies.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { logAnalyticsEvent } from "../../Firebase";
import "./Public.css";

import { CASE_STUDIES } from "../../data/caseStudies";
import useSeo from "../../hooks/useSeo";

const pubUrl = process.env.PUBLIC_URL || "";

export default function CaseStudies() {
    useSeo({
  title: "Portfolio & Case Studies | Crowned Roofing",
  description:
    "Real-world roofing case studies: inspections, leak investigations, targeted repairs, and condition-based recommendations across commercial and residential projects.",
  ogImage: "https://www.crownedroofing.com/images/ogimg.png",
  canonical: "https://www.crownedroofing.com/portfolio",
});


  const navigate = useNavigate();
  const [filter, setFilter] = useState("all"); // all | commercial | residential
  const [query, setQuery] = useState("");

  useEffect(() => {
    document.title = "Case Studies | Crowned Roofing";
    const meta = document.querySelector("meta[name='description']");
    if (meta) {
      meta.setAttribute(
        "content",
        "Commercial and residential roofing case studies focused on documented conditions, practical scopes, and clear outcomes."
      );
    }
  }, []);

  // Data source (imported from separate file)
  const studies = useMemo(() => CASE_STUDIES, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return studies
      .filter((s) => (filter === "all" ? true : s.type === filter))
      .filter((s) => {
        if (!q) return true;
        const blob =
          `${s.title} ${s.location} ${s.tags.join(" ")} ${s.situation} ${s.approach} ${s.outcome}`.toLowerCase();
        return blob.includes(q);
      });
  }, [studies, filter, query]);

  const openStudy = (slug) => {
    logAnalyticsEvent("case_study_open", {
      slug,
      source: "case_studies_page",
    });
    navigate(`/case-studies/${slug}`);
  };

  const requestAssessment = () => {
    logAnalyticsEvent("case_studies_cta_click", {
      source: "case_studies_page",
    });
    navigate("/contact");
  };

  return (
    <div className="csPage">
      <Header />

      {/* HERO */}
      <section className="csHero">
        <div
          className="csHeroBg"
          aria-hidden="true"
          style={{
            backgroundImage: `url(${pubUrl}/images/case-studies/case-hero.jpg)`,
          }}
        />
        <div className="csHeroShade" aria-hidden="true" />

        <div className="csHeroInner">
          <div className="csHeroCopy">
            <h1>Portfolio</h1>
            <p>
              A selection of completed projects showing documented conditions,
              practical scopes, and clear outcomes—across commercial and
              residential roofing.
            </p>

            <button className="csBtn csBtnPrimary" onClick={requestAssessment}>
              REQUEST AN ASSESSMENT
            </button>
          </div>
        </div>
      </section>

      {/* CONTROLS */}
      <section className="csControls">
        <div className="csContainer">
          <div className="csToolbar">
            <div className="csTabs" role="tablist" aria-label="Case study filters">
              <button
                className={`csTab ${filter === "all" ? "active" : ""}`}
                onClick={() => setFilter("all")}
                role="tab"
                aria-selected={filter === "all"}
                type="button"
              >
                All
              </button>
              <button
                className={`csTab ${filter === "commercial" ? "active" : ""}`}
                onClick={() => setFilter("commercial")}
                role="tab"
                aria-selected={filter === "commercial"}
                type="button"
              >
                Commercial
              </button>
              <button
                className={`csTab ${filter === "residential" ? "active" : ""}`}
                onClick={() => setFilter("residential")}
                role="tab"
                aria-selected={filter === "residential"}
                type="button"
              >
                Residential
              </button>
            </div>

            <div className="csSearch">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by project type, location, or keyword…"
                aria-label="Search case studies"
              />
            </div>
          </div>

          <div className="csCount">
            Showing <strong>{filtered.length}</strong> project
            {filtered.length === 1 ? "" : "s"}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="csGridSection">
        <div className="csContainer">
          <div className="csGrid">
            {filtered.map((c) => (
              <article className="csCard" key={c.slug}>
                <div className="csCardTop">
                  <div className={`csPill ${c.type}`}>
                    {c.type === "commercial" ? "Commercial" : "Residential"}
                  </div>
                  <h2 className="csTitle">{c.title}</h2>
                  <div className="csLoc">{c.location}</div>
                </div>

                <div className="csImgWrap">
                  <img src={c.heroImg} alt="" aria-hidden="true" />
                </div>

                <div className="csTags">
                  {Array.isArray(c.tags) &&
                    c.tags.map((t) => (
                      <span className="csTag" key={t}>
                        {t}
                      </span>
                    ))}
                </div>

                <div className="csBody">
                  <div className="csRow">
                    <div className="csKicker">Situation</div>
                    <p>{c.situation}</p>
                  </div>
                  <div className="csRow">
                    <div className="csKicker">Scope</div>
                    <p>{c.scope}</p>
                  </div>
                  <div className="csRow">
                    <div className="csKicker">Outcome</div>
                    <p>{c.outcome}</p>
                  </div>
                </div>

                <div className="csActions">
                  <button
                    className="csBtn csBtnGhost"
                    onClick={() => openStudy(c.slug)}
                    type="button"
                  >
                    VIEW CASE STUDY →
                  </button>
                </div>
              </article>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="csEmpty">
              <div className="csEmptyTitle">No matching case studies</div>
              <div className="csEmptyText">
                Try clearing the search or switching filters.
              </div>
              <button
                className="csBtn csBtnPrimary"
                onClick={() => {
                  setQuery("");
                  setFilter("all");
                }}
                type="button"
              >
                RESET
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="csCta">
        <div className="csContainer">
          <div className="csCtaCard">
            <h2>Need clarity before making a decision?</h2>
            <p>
              If you’re deciding between targeted repairs, ongoing maintenance,
              or replacement planning, we can evaluate existing conditions and
              define a scope based on documented findings.
            </p>
            <button className="csBtn csBtnPrimary" onClick={requestAssessment} type="button">
              REQUEST AN ASSESSMENT
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
